import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  Button,
  Dialog,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { LanguageContext } from "../../context/LanguageContext";

const labels = {
  en: {
    viewDetails: "Open case",
    visitCompany: "Visit site",
    scale: "Impact",
    stack: "Stack",
    close: "Close",
  },
  pt: {
    viewDetails: "Abrir caso",
    visitCompany: "Visitar site",
    scale: "Impacto",
    stack: "Stack",
    close: "Fechar",
  },
};

const ProjectCard = ({ project, index = 0 }) => {
  const [open, setOpen] = useState(false);
  const { language } = useContext(LanguageContext);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const t = labels[language] || labels.en;
  const number = String(index + 1).padStart(2, "0");
  const pointer = useRef({ x: 0, y: 0 });

  useEffect(() => {
    document.body.classList.toggle("case-open", open);
    return () => document.body.classList.remove("case-open");
  }, [open]);

  const openIfTap = (event) => {
    if (Math.abs(event.clientX - pointer.current.x) > 8 || Math.abs(event.clientY - pointer.current.y) > 8) {
      return;
    }
    setOpen(true);
  };

  return (
    <>
      <Box
        component="article"
        onPointerDown={(event) => {
          pointer.current = { x: event.clientX, y: event.clientY };
        }}
        onClick={openIfTap}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.4,
          height: "100%",
          minHeight: 220,
          cursor: "pointer",
          touchAction: "manipulation",
          background: "rgba(198, 164, 106, 0.05)",
          border: "1px solid rgba(198, 164, 106, 0.18)",
          borderRadius: 3,
          p: { xs: 2.6, md: 3 },
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "@media (hover: hover)": {
            transition: "transform 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease",
            "&:hover": {
              transform: "translateY(-6px)",
              borderColor: "rgba(198, 164, 106, 0.55)",
              boxShadow: "0 0 36px rgba(198, 164, 106, 0.16)",
            },
          },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography
            variant="caption"
            sx={{
              color: "#c6a46a",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: "0.68rem",
            }}
          >
            {project.sector}
          </Typography>
          <Typography
            sx={{
              color: "rgba(198,164,106,0.55)",
              fontSize: "0.8rem",
              letterSpacing: "0.08em",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {number}
          </Typography>
        </Box>

        <Typography variant="h5" component="h3" sx={{ fontWeight: 700, letterSpacing: -0.3, color: "#f3efe6" }}>
          {project.company}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(243,239,230,0.64)", lineHeight: 1.6 }}>
          {project.title}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {project.role}
        </Typography>

        <Box
          sx={{
            mt: "auto",
            pt: 2,
            borderTop: "1px solid rgba(198, 164, 106, 0.12)",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#c6a46a", flexShrink: 0 }} />
          <Typography sx={{ fontSize: "0.75rem", color: "#c6a46a", letterSpacing: 0.3 }}>
            {t.viewDetails} →
          </Typography>
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        scroll="paper"
        slotProps={{
          root: { sx: { zIndex: 2000 } },
          backdrop: { sx: { backgroundColor: "rgba(8,7,5,0.78)" } },
        }}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            bgcolor: "#0f0e0c",
            backgroundImage: "none",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            border: fullScreen ? "none" : "1px solid rgba(243,239,230,0.08)",
          },
        }}
      >
        <Box sx={{ position: "relative", pl: { xs: 2.5, sm: 4 }, pr: { xs: 7, sm: 8 }, py: { xs: 3, sm: 4 } }}>
          <IconButton
            onClick={() => setOpen(false)}
            aria-label={t.close}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#f3efe6",
              border: "1px solid rgba(243,239,230,0.16)",
              "&:hover": { borderColor: "#c6a46a", color: "#c6a46a" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            sx={{
              color: "#c6a46a",
              textTransform: "uppercase",
              letterSpacing: 1.2,
              fontWeight: 600,
            }}
          >
            {project.sector} · {project.type}
          </Typography>
          <Typography variant="h4" component="h2" sx={{ mt: 0.8, mb: 0.5, fontWeight: 700 }}>
            {project.company}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
            {project.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {project.role}
          </Typography>

          <Typography
            variant="body1"
            sx={{ mt: 3, mb: 3, color: "rgba(243,239,230,0.78)", lineHeight: 1.7 }}
          >
            {project.description}
          </Typography>

          <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", display: "grid", gap: 1.25 }}>
            {project.bullets.map((bullet) => (
              <Box
                component="li"
                key={bullet}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "10px 1fr",
                  gap: 1.25,
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "#c6a46a",
                    mt: 1,
                  }}
                />
                <Typography variant="body2" sx={{ lineHeight: 1.65, color: "rgba(243,239,230,0.82)" }}>
                  {bullet}
                </Typography>
              </Box>
            ))}
          </Box>

          {project.scale && (
            <Box
              mt={3.5}
              p={2}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(198,164,106,0.22)",
                background: "rgba(198,164,106,0.06)",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#c6a46a",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                }}
              >
                {t.scale}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.6, lineHeight: 1.55 }}>
                {project.scale}
              </Typography>
            </Box>
          )}

          <Box mt={3.5}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.8,
                display: "block",
                mb: 1.2,
              }}
            >
              {t.stack}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.8}>
              {project.stack.map((tech) => (
                <Chip
                  key={tech}
                  label={tech}
                  size="small"
                  sx={{
                    fontSize: "0.72rem",
                    height: 24,
                    color: "rgba(243,239,230,0.75)",
                    border: "1px solid rgba(243,239,230,0.12)",
                    bgcolor: "transparent",
                  }}
                />
              ))}
            </Box>
          </Box>

          {project.companyLink && (
            <Box mt={4} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                disableElevation
                href={project.companyLink}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<LaunchIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  px: 2.2,
                  bgcolor: "#c6a46a",
                  color: "#14110d",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#d4b57a" },
                }}
              >
                {t.visitCompany}
              </Button>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
};

export default ProjectCard;
